import { Box, Button, Divider, Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { useBoundingBox } from 'modules/Editor/components/BoundingBox/useBoundingBox';
import { useAppSelector } from 'modules/Editor/store';
import { selectHasActiveWidget } from 'modules/Editor/store/widgetSelector';
import { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { alignWidgets } from './Alignment.helpers';
import { AlignmentOption, DistributeOption } from './AlignmentMenu.types';
import { AlignmentOptions } from './AlignmentOptions';
import { distributeWidgets } from './Distribute.helpers';
import { DistributeOptions } from './DistributeOptions';

const ALIGN_MENU_BTN_ID = 'align-menu-btn';

export const AlignmentMenu = memo(() => {
  const { t } = useTranslation('editor_toolbar', { useSuspense: false, keyPrefix: 'alignment' });
  const { isOpen, onClose, onToggle } = useDisclosure();
  const hasActiveWidget = useAppSelector(selectHasActiveWidget);
  const { boundingBox } = useBoundingBox();

  const popoverContentRef = useRef<HTMLDivElement>(null);

  // Disable if no widgets are selected
  const isDisabled = !hasActiveWidget;

  useOutsideClick({
    ref: popoverContentRef,
    handler: onClose,
    ignoreId: ALIGN_MENU_BTN_ID,
  });

  const onAlign = (alignOption: AlignmentOption) => {
    alignWidgets(alignOption);
    setTimeout(() => boundingBox.updateRect(), 0);
  };

  const onDistribute = (distributeOption: DistributeOption) => {
    distributeWidgets(distributeOption);
    setTimeout(() => boundingBox.updateRect(), 0);
  };

  const label = t('label');

  return (
    <Popover placement='bottom-end' isOpen={isOpen} onClose={onClose} lazyBehavior='unmount' isLazy closeOnBlur>
      <Box id={ALIGN_MENU_BTN_ID}>
        <PopoverTrigger>
          <Button variant='ghost-with-active' size='sm' isDisabled={isDisabled} isActive={isOpen} onClick={onToggle}>
            {label}
          </Button>
        </PopoverTrigger>
      </Box>
      <PopoverContent p={3} mt='1px' color='font.500' w='fit-content' ref={popoverContentRef}>
        <AlignmentOptions onAlign={onAlign} />
        <Divider mt={2} />
        <DistributeOptions onDistribute={onDistribute} />
      </PopoverContent>
    </Popover>
  );
});
AlignmentMenu.displayName = 'AlignmentMenu';
